import { useState, type ComponentProps } from 'react';
import type {
  Metafield,
  Task,
  TaskPriority,
  TaskStatus,
  TaskTrackerValues,
} from '@forecastify/shared';
import type { DataList, SortState } from '@forecastify/ui';
import { TaskDialog, type TaskDialogLineItem, type TaskDraft } from './task-dialog';
import { TasksScreen, type TaskFilters } from './tasks-screen';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The project's Feladatok tab — C7's debt, paid by C8.                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **C7 shipped the project detail without this tab and said so on the page**, because tasks did
 * not exist. `chunks.md` records the debt against C8; this is it.
 *
 * ⚠️⚠️ **IT IS ALL FOUR OF C9'S VIEWS NOW — item 30, and this file predicted it.** The note that
 * stood here read: *"this is NOT one of C9's four task views … when C9 lands, the switcher goes
 * above this and the columns stay"*. C9 landed and it never did, so a project's tasks were a bare
 * list while the same tasks one screen over had a board, a grid and a timeline.
 *
 * ⚠️⚠️ **`TasksScreen` EMBEDDED, NOT A FIFTH COPY OF THE FOUR VIEWS.** The board, the timeline and
 * the grid are three hundred lines each, and every one of them would have drifted from its twin —
 * the exact failure `task-columns.ts` was extracted to prevent one level down. `embedded` is what
 * stops it claiming the shell's header, which the PROJECT screen owns.
 *
 * ⚠️ **The project id is pinned in the FILTERS**, so every view — including the board's columns and
 * the timeline's range — is narrowed the same way, by the same clause, on the server.
 */
export function ProjectTasksTab({
  tasks,
  loading,
  statuses,
  priorities,
  assignees,
  teams = [],
  othersFilterable,
  canCreate,
  onCreate,
  lineItems,
  projectId,
  onOpen,
  serverError,
  page,
  filters,
  onFiltersChange,
  sort,
  onSortChange,
  metafields,
  trackerValues,
  canEdit,
  onMoveStatus,
  onReschedule,
  onGridEdit,
  onExport,
  onImport,
}: {
  readonly tasks: readonly Task[];
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly statuses: readonly TaskStatus[];
  readonly priorities: readonly TaskPriority[];
  readonly assignees: readonly { id: string; name: string }[];
  /** R-60 · the assignee's team, as a filter. Absent where nothing passes the list. */
  readonly teams?: readonly { readonly id: string; readonly name: string }[];
  /** 3 Nov 2026 · `false` hides the assignee and team filters — an own-scope reader. */
  readonly othersFilterable?: boolean | undefined;
  readonly canCreate: boolean;
  /**
   * ⚠️⚠️ **RESOLVES TO WHETHER THE TASK WAS ACTUALLY CREATED.** It returned `void`, and this
   * tab closed the dialog on submit either way — so a refusal discarded the form and everything
   * typed into it, leaving an error message beside a dialog that was no longer there.
   */
  readonly onCreate: (draft: TaskDraft) => Promise<boolean>;
  /** ⚠️ C25 · this project's line items. Empty draws no picker. */
  readonly lineItems: readonly TaskDialogLineItem[];
  /**
   * ⚠️ **The project every task created here belongs to.** Passed instead of a project PICKER: the
   * answer is not in question on a project's own page, and a control offering to move the task
   * somewhere else would be a control nobody wants on this screen.
   */
  readonly projectId: string;
  readonly onOpen: (task: Task) => void;
  readonly serverError: string | null;
  /* ⚠️ `total` and `openTotal` went with the footer note — item 30. `TasksScreen`'s pager carries
     the filtered total now, which is the same figure and one control closer to it. */
  /** ⚠️ The list's pages. Absent means the caller loads everything — see the footer. */
  readonly page?: ComponentProps<typeof DataList>['page'];
  /**
   * ⚠️ **Item 30 · what the four views need.** Every one of these already exists on the global
   * tasks route; the project route now supplies the same, narrowed to this project.
   */
  readonly filters: TaskFilters;
  readonly onFiltersChange: (filters: TaskFilters) => void;
  readonly sort: readonly SortState[];
  /** The workspace's task metafields — the same columns and filters `/tasks` offers. */
  readonly metafields: readonly Metafield[];
  /** J-1 · the tracker values this project's synced tasks carry — `/tasks`'s three filters. */
  readonly trackerValues?: TaskTrackerValues;
  readonly onSortChange: (next: readonly SortState[]) => void;
  readonly canEdit: boolean;
  readonly onMoveStatus: (task: Task, statusId: string) => void;
  readonly onReschedule: (task: Task, startsOn: string, dueOn: string) => void;
  readonly onGridEdit: (task: Task, column: string, value: string) => void;
  readonly onExport: () => void;
  readonly onImport: () => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  /* ⚠️ The list's own search, status filter, columns and totals footer went with item 30 —
     `TasksScreen` owns all four now, and its toolbar is the one the global screen uses. */

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/**
       * ⚠️⚠️ **`TasksScreen`, EMBEDDED — item 30.** Its own header note carries the argument for
       * why this is not a fifth copy of the board, the grid and the timeline.
       *
       * ⚠️ **The project is pinned in the FILTERS**, so every view is narrowed by the same clause
       * on the server — including the board's columns and the timeline's range, which a
       * client-side filter could not reach.
       *
       * ⚠️ **`onCreate` opens THIS tab's dialog**, not the global one: a task made here needs the
       * project's line items and needs no project picker, which is what `TaskDialog` below is for.
       */}
      <TasksScreen
        embedded
        tasks={tasks}
        loading={loading === true}
        statuses={statuses}
        priorities={priorities}
        projects={[]}
        members={assignees}
        teams={teams}
        {...(othersFilterable === undefined ? {} : { othersFilterable })}
        /**
         * ⚠️⚠️ **THE PROJECT IS `scopeProjectId`, NOT A FILTER — and folding it into `filters` was
         * the bug.** With it in there the screen believed a filter was set, so every empty board
         * column announced *"a szűrő minden feladatot elrejtett"* on a project that simply has
         * nothing in that status. `tasks-screen.tsx` carries the argument at greater length.
         */
        filters={filters}
        scopeProjectId={projectId}
        onFiltersChange={onFiltersChange}
        sort={sort}
        onSortChange={onSortChange}
        metafields={metafields}
        {...(trackerValues === undefined ? {} : { trackerValues })}
        canCreate={canCreate}
        canEdit={canEdit}
        onCreate={() => {
          setDialogOpen(true);
        }}
        onOpen={onOpen}
        onMoveStatus={onMoveStatus}
        onReschedule={onReschedule}
        onGridEdit={onGridEdit}
        onExport={onExport}
        onImport={onImport}
        /**
         * ⚠️⚠️ **ONE ERROR, SHOWN WHERE THE PERSON IS LOOKING.** Both this screen and the dialog
         * below render `serverError`, so a refused create printed it twice — once behind the
         * dialog and once inside it. While the dialog is open it owns the sentence, because that
         * is where the form it refers to is; the rest of the time the screen does, because a grid
         * edit or a drag has no dialog to report into.
         */
        serverError={dialogOpen ? null : serverError}
        {...(page === undefined ? {} : { page })}
      />

      <TaskDialog
        open={dialogOpen}
        statuses={statuses}
        priorities={priorities}
        assignees={assignees}
        lineItems={lineItems}
        projectId={projectId}
        serverError={serverError}
        onClose={() => {
          setDialogOpen(false);
        }}
        /**
         * ⚠️⚠️ **CLOSED ONLY WHEN THE SERVER SAYS IT WORKED.** This used to close
         * unconditionally, which is how a `400` on create became a dialog that vanished with the
         * work still in it. The error already renders inside the dialog — it just had nowhere left
         * to render.
         */
        onSubmit={(draft) => {
          void onCreate(draft).then((created) => {
            if (created) setDialogOpen(false);
          });
        }}
      />
    </div>
  );
}
